import type { Scene } from '../content'

/**
 * Presenter view, in a separate browser window.
 *
 * The proposal is delivered as a recorded video, so presenter notes must never appear in the
 * captured window. This opens a second window holding the notes, a running clock and the
 * controls, leaving the presentation window clean for screen capture.
 *
 * Written as plain DOM rather than a React portal: a portal into another document does not
 * carry this app's stylesheets, and a self-contained document is more robust across the
 * popup lifecycle.
 */

export interface PresenterPayload {
  scene: Scene
  index: number
  total: number
  chapterTitle: string
  transitionIn?: string
  elapsedEstimate: number
  totalEstimate: number
  /** Sum of estimates for this scene and every scene after it. */
  scriptedRemaining: number
  /** Target delivered length, in seconds. */
  target: number
  next?: { title: string; headline: string }
}

const DOC = `<!doctype html>
<html lang="en"><head><meta charset="utf-8" />
<title>LAMS — Presenter View</title>
<style>
  :root { color-scheme: dark; }
  * { box-sizing: border-box; }
  body {
    margin: 0; padding: 20px 24px;
    background: #0b2029; color: #e3edef;
    font: 15px/1.55 Inter, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
  }
  .top { display: flex; align-items: baseline; gap: 14px; flex-wrap: wrap;
         padding-bottom: 12px; border-bottom: 1px solid #22414d; }
  .chapter { font-size: 11px; font-weight: 700; letter-spacing: .12em;
             text-transform: uppercase; color: #7db9cd; }
  .count { font-family: ui-monospace, Menlo, Consolas, monospace; font-size: 12px; color: #88a4ad; }
  .id { font-family: ui-monospace, Menlo, Consolas, monospace; font-size: 11px;
        padding: 2px 7px; background: #163643; border-radius: 4px; color: #a8c0c7; }
  .clock { margin-left: auto; text-align: right; }
  .clock b { display: block; font-family: ui-monospace, Menlo, Consolas, monospace;
             font-size: 30px; font-weight: 600; letter-spacing: -.02em; line-height: 1.1; }
  .clock span { font-size: 11px; color: #88a4ad; }
  .pace { display: flex; gap: 18px; align-items: baseline; justify-content: flex-end;
          margin-top: 6px; font-size: 11px; }
  .pace div { text-align: right; }
  .pace em { display: block; font-style: normal; color: #6d8992; font-size: 10px;
             letter-spacing: .06em; text-transform: uppercase; }
  .pace strong { font-family: ui-monospace, Menlo, Consolas, monospace;
                 font-size: 15px; font-weight: 600; color: #cbdade; }
  .pace.ok strong#projected { color: #9cc4a3; }
  .pace.tight strong#projected { color: #d9bd86; }
  .pace.over strong#projected { color: #e8a87c; }
  h1 { margin: 18px 0 6px; font-size: 24px; line-height: 1.15; letter-spacing: -.02em; color: #f2f7f8; }
  .support { margin: 0 0 14px; font-size: 14px; color: #a8c0c7; }
  .transition { margin: 0 0 14px; padding: 9px 12px; border-radius: 6px;
                background: rgba(46,135,164,.16); font-style: italic; font-size: 14px; color: #cbdade; }
  .transition i { display: block; font-style: normal; font-size: 10px; font-weight: 700;
                  letter-spacing: .07em; text-transform: uppercase; color: #7db9cd; margin-bottom: 3px; }
  .label { font-size: 10px; font-weight: 700; letter-spacing: .1em;
           text-transform: uppercase; color: #d9bd86; margin-bottom: 8px; }
  ul.notes { margin: 0 0 18px; padding-left: 20px; }
  ul.notes li { margin-bottom: 9px; color: #cbdade; }
  .next { padding-top: 12px; border-top: 1px solid #22414d; font-size: 13px; color: #88a4ad; }
  .next i { font-style: normal; font-weight: 700; font-size: 10px; letter-spacing: .07em;
            text-transform: uppercase; color: #7db9cd; margin-right: 8px; }
  .bar { display: flex; gap: 8px; margin-top: 18px; padding-top: 14px; border-top: 1px solid #22414d; }
  button { flex: 1; padding: 11px; background: #163643; color: #e3edef;
           border: 1px solid #335865; border-radius: 6px; font: inherit; font-weight: 600; cursor: pointer; }
  button:hover { background: #1c3f4f; }
  button.wide { flex: 2; }
  .hint { margin-top: 10px; font-size: 11px; color: #6d8992; text-align: center; }
</style></head>
<body>
  <div class="top">
    <span class="chapter" id="chapter"></span>
    <span class="count"><span id="n"></span> / <span id="total"></span></span>
    <span class="id" id="sid"></span>
    <span class="clock" id="clock"><b id="elapsed">0:00</b><span id="budget"></span></span>
  </div>
  <div class="pace" id="pace">
    <div><em>Script left</em><strong id="remaining">—</strong></div>
    <div><em>Projected finish</em><strong id="projected">—</strong></div>
    <div><em>Target</em><strong id="target">60:00</strong></div>
  </div>
  <h1 id="headline"></h1>
  <p class="support" id="support"></p>
  <div id="transition"></div>
  <div class="label">Presenter notes</div>
  <ul class="notes" id="notes"></ul>
  <div class="next"><i>Next</i><span id="next"></span></div>
  <div class="bar">
    <button id="prev">&larr; Previous</button>
    <button id="play" class="wide">Start timer</button>
    <button id="next-btn">Next &rarr;</button>
  </div>
  <p class="hint">This window is for you. Capture the other window only.</p>
</body></html>`

function fmt(total: number): string {
  const sign = total < 0 ? '-' : ''
  const t = Math.abs(Math.round(total))
  return `${sign}${Math.floor(t / 60)}:${String(t % 60).padStart(2, '0')}`
}

export class PresenterWindow {
  private win: Window | null = null
  private timerStart: number | null = null
  private tick: number | null = null
  private onNav: (dir: -1 | 1) => void

  constructor(onNav: (dir: -1 | 1) => void) {
    this.onNav = onNav
  }

  get isOpen(): boolean {
    return !!this.win && !this.win.closed
  }

  open(payload: PresenterPayload): boolean {
    if (this.isOpen) {
      this.win!.focus()
      return true
    }
    const win = window.open('', 'lams-presenter', 'width=560,height=760')
    if (!win) return false /* blocked by the popup blocker */
    win.document.open()
    win.document.write(DOC)
    win.document.close()
    this.win = win

    const bind = (id: string, fn: () => void) => win.document.getElementById(id)?.addEventListener('click', fn)
    bind('prev', () => this.onNav(-1))
    bind('next-btn', () => this.onNav(1))
    bind('play', () => this.toggleTimer())

    /* Let the presenter drive from whichever window has focus. */
    win.addEventListener('keydown', (e: KeyboardEvent) => {
      if (e.key === 'ArrowRight' || e.key === ' ') {
        e.preventDefault()
        this.onNav(1)
      } else if (e.key === 'ArrowLeft') {
        e.preventDefault()
        this.onNav(-1)
      }
    })

    this.tick = win.setInterval(() => this.paintClock(), 500)
    this.update(payload)
    return true
  }

  private toggleTimer(): void {
    this.timerStart = this.timerStart === null ? Date.now() : null
    const btn = this.win?.document.getElementById('play')
    if (btn) btn.textContent = this.timerStart === null ? 'Start timer' : 'Reset timer'
    this.paintClock()
  }

  private budget = { elapsed: 0, total: 0, remaining: 0, target: 3600 }

  private paintClock(): void {
    if (!this.isOpen) return
    const d = this.win!.document
    const el = d.getElementById('elapsed')
    const clock = d.getElementById('clock')
    if (!el || !clock) return
    const pace = d.getElementById('pace')
    const projected = d.getElementById('projected')

    if (this.timerStart === null) {
      el.textContent = fmt(this.budget.elapsed)
      clock.className = 'clock'
      if (projected) projected.textContent = fmt(this.budget.total)
      if (pace) pace.className = this.budget.total > this.budget.target ? 'pace over' : 'pace ok'
      return
    }

    const real = (Date.now() - this.timerStart) / 1000
    el.textContent = fmt(real)
    clock.className = 'clock'

    /*
     * Projected finish is what actually matters while recording: real time already spent,
     * plus the scripted time still ahead. Flagged against the target.
     */
    const finish = real + this.budget.remaining
    if (projected) projected.textContent = fmt(finish)
    if (pace) {
      const ratio = finish / this.budget.target
      pace.className = ratio > 1 ? 'pace over' : ratio > 0.95 ? 'pace tight' : 'pace ok'
    }
  }

  update(p: PresenterPayload): void {
    if (!this.isOpen) return
    const d = this.win!.document
    const set = (id: string, text: string) => {
      const node = d.getElementById(id)
      if (node) node.textContent = text
    }
    this.budget = {
      elapsed: p.elapsedEstimate,
      total: p.totalEstimate,
      remaining: p.scriptedRemaining,
      target: p.target,
    }
    set('remaining', fmt(p.scriptedRemaining))
    set('target', fmt(p.target))

    set('chapter', p.chapterTitle)
    set('n', String(p.index + 1))
    set('total', String(p.total))
    set('sid', p.scene.id)
    set('headline', p.scene.headline)
    set('support', p.scene.supporting ?? '')
    set('budget', `scripted ${fmt(p.elapsedEstimate)} · ${p.scene.estimatedSeconds}s this scene · ${fmt(p.totalEstimate)} total`)

    const trans = d.getElementById('transition')
    if (trans) {
      trans.innerHTML = ''
      if (p.transitionIn) {
        const box = d.createElement('div')
        box.className = 'transition'
        const lab = d.createElement('i')
        lab.textContent = 'Transition in'
        box.appendChild(lab)
        box.appendChild(d.createTextNode(p.transitionIn))
        trans.appendChild(box)
      }
    }

    const notes = d.getElementById('notes')
    if (notes) {
      notes.innerHTML = ''
      for (const n of p.scene.presenterNotes) {
        const li = d.createElement('li')
        li.textContent = n
        notes.appendChild(li)
      }
    }

    set('next', p.next ? `${p.next.title} — ${p.next.headline}` : 'Questions.')
    this.paintClock()
  }

  close(): void {
    if (this.tick !== null && this.win) this.win.clearInterval(this.tick)
    this.tick = null
    if (this.isOpen) this.win!.close()
    this.win = null
    this.timerStart = null
  }
}
